import { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { usePropertyPurchases, useUpdatePurchaseStatus, useApproveAndPay, useTryAutopay, useMarkPurchaseAutopay, useMarkPurchaseDelivered, OwnerPurchaseWithDetails } from "@/hooks/useOwnerPurchases";
import { usePayeeConnectStatus } from "@/hooks/usePayeeConnectStatus";
import { useProperty, useUpdateProperty } from "@/hooks/useProperties";
import { useBillingProfile, useCreateSetupIntent, useSavePaymentMethod, useSetDefaultPaymentMethod, type BillingProfilePaymentMethod } from "@/hooks/useBillingProfile";
import {
  usePropertyBillingSettings,
  useUpdatePropertyBillingPreferences,
  usePropertyReimbursements,
  useApproveInventoryReimbursement,
  useRejectInventoryReimbursement,
  type InvoiceFrequency,
  type InventoryReimbursement,
} from "@/hooks/useInventoryReimbursements";
import {
  usePropertyCleaningAgreement,
  useUpsertPropertyCleaningAgreement,
  type PayoutSchedule,
  type PaymentMode,
  type AgreementCurrency,
} from "@/hooks/usePropertyCleaningAgreement";
import { useInvoicesByProperty, useCreateInvoiceFromPeriod } from "@/hooks/useInvoices";
import { formatMoney } from "@/lib/formatMoney";
import { format, parseISO } from "date-fns";
import { Check, X, DollarSign, Package, Receipt, AlertCircle, Image, ExternalLink, ShoppingCart, CreditCard, Loader2, Zap, Upload, Truck, FileText, CalendarIcon, ChevronDown, ChevronRight, Lock } from "lucide-react";
import { toast } from "sonner";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { StripeCardForm } from "./StripeCardForm";
import { InvoiceDetailView } from "./InvoiceDetailView";
import { InventoryReimbursementInfo } from "./InventoryReimbursementInfo";
import { toast as toastHook } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { sendInvoiceReadyEmail, getBaseUrl, getInvoiceRecipientEmails } from "@/lib/notificationEmails";
import { supabase } from "@/integrations/supabase/client";
import { usePropertyAccessAndInvites } from "@/hooks/useMemberInvites";
import { usePropertyAssignableMembers } from "@/hooks/usePropertyAssignableMembers";
import {
  canManagePaymentsOwnerOnly,
  canEditPaymentsRates,
  isPaymentsReadOnly,
  canPerformNonPaymentInventoryActions,
  type PropertyUserRole,
  type PropertyAccessLevel,
} from "@/lib/propertyTabs";

// Load both test and live so we can confirm the SetupIntent in the same mode the backend used (avoids 404 "No such setup intent").
const STRIPE_PUBLISHABLE_KEY_TEST = import.meta.env.VITE_STRIPE_TEST_PUBLISHABLE_KEY || "";
const STRIPE_PUBLISHABLE_KEY_LIVE = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || "";
const stripePromiseTest = STRIPE_PUBLISHABLE_KEY_TEST ? loadStripe(STRIPE_PUBLISHABLE_KEY_TEST) : null;
const stripePromiseLive = STRIPE_PUBLISHABLE_KEY_LIVE ? loadStripe(STRIPE_PUBLISHABLE_KEY_LIVE) : null;
// Default for display / fallback
const STRIPE_PUBLISHABLE_KEY = STRIPE_PUBLISHABLE_KEY_TEST || STRIPE_PUBLISHABLE_KEY_LIVE;

interface PaymentsTabProps {
  propertyId: string;
  propertyName?: string;
  /** When false, payment method and pay toggles are owner-only (team sees locked read-only blocks). */
  isPropertyOwner?: boolean;
  role?: PropertyUserRole | null;
  accessLevel?: PropertyAccessLevel | null;
}

export default function PaymentsTab({
  propertyId,
  propertyName,
  isPropertyOwner = true,
  role = "owner",
  accessLevel = "full",
}: PaymentsTabProps) {
  const canManagePaymentMethodAndToggles = canManagePaymentsOwnerOnly(role ?? "owner", accessLevel ?? "full");
  const canEditRates = canEditPaymentsRates(role ?? "owner", accessLevel ?? "full");
  const isReadOnlyPayments = isPaymentsReadOnly(role ?? "owner", accessLevel ?? "full");
  const canPerformNonPaymentActions = canPerformNonPaymentInventoryActions(role ?? "owner", accessLevel ?? "full");
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const invoiceId = searchParams.get("invoiceId") ?? null;
  const { user, profile } = useAuth();

  const { data: property } = useProperty(propertyId);
  const { data: billingSettings } = usePropertyBillingSettings(propertyId);
  const updateBillingPreferences = useUpdatePropertyBillingPreferences(propertyId);
  const updateProperty = useUpdateProperty();
  const { data: agreement } = usePropertyCleaningAgreement(propertyId);
  const upsertAgreement = useUpsertPropertyCleaningAgreement(propertyId);
  const { data: accessAndInvites } = usePropertyAccessAndInvites(propertyId, user?.id ?? undefined);
  const { members: assignableMembers } = usePropertyAssignableMembers(propertyId);
  const cleanersForPayee = assignableMembers.filter((m) => m.role === "Cleaner");
  const members = (accessAndInvites?.members ?? []) as Array<{ grantee_type?: string; grantee_company_id?: string; grantee_user_id?: string }>;
  const companyIdsFromAccess = members
    .filter((m) => m.grantee_type === "company" && m.grantee_company_id)
    .map((m) => m.grantee_company_id!);
  const teamAdminUserIds = members
    .filter((m) => m.grantee_type === "user" && m.grantee_user_id)
    .map((m) => m.grantee_user_id!);
  const { data: companiesForPayee = [] } = useQuery({
    queryKey: ["cleaning-companies-payee", companyIdsFromAccess, teamAdminUserIds],
    queryFn: async () => {
      const ids = new Set(companyIdsFromAccess);
      if (teamAdminUserIds.length > 0) {
        const { data: companiesByAdmin } = await supabase
          .from("cleaning_companies")
          .select("id, company_name")
          .in("admin_user_id", teamAdminUserIds);
        (companiesByAdmin ?? []).forEach((c: { id: string }) => ids.add(c.id));
      }
      if (ids.size === 0) return [];
      const { data, error } = await supabase
        .from("cleaning_companies")
        .select("id, company_name")
        .in("id", [...ids]);
      if (error) throw error;
      return (data ?? []) as Array<{ id: string; company_name: string | null }>;
    },
    enabled: companyIdsFromAccess.length > 0 || teamAdminUserIds.length > 0,
  });
  const { data: purchases, isLoading: purchasesLoading } = usePropertyPurchases(propertyId);
  const { data: legacyReimbursements = [], isLoading: reimbursementsLoading } = usePropertyReimbursements(propertyId);
  const approveReimbursement = useApproveInventoryReimbursement();
  const rejectReimbursement = useRejectInventoryReimbursement();
  const { data: billingProfile, isLoading: billingLoading, error: billingError } = useBillingProfile();
  const createSetupIntent = useCreateSetupIntent();
  const savePaymentMethod = useSavePaymentMethod();
  const setDefaultPaymentMethod = useSetDefaultPaymentMethod();
  const updateStatus = useUpdatePurchaseStatus();
  const approveAndPay = useApproveAndPay();
  const tryAutopay = useTryAutopay();
  const markAutopay = useMarkPurchaseAutopay();
  const markDelivered = useMarkPurchaseDelivered();

  const [selectedPurchase, setSelectedPurchase] = useState<OwnerPurchaseWithDetails | null>(null);
  const [selectedReimbursement, setSelectedReimbursement] = useState<InventoryReimbursement | null>(null);
  const [showCardModal, setShowCardModal] = useState(false);
  const [cardSetupData, setCardSetupData] = useState<{ clientSecret: string; setupIntentId: string; stripe_mode: "test" | "live" } | null>(null);
  const [cardInfo, setCardInfo] = useState<{ brand: string; last4: string; expMonth?: number; expYear?: number } | null>(null);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [autopaySkipReason, setAutopaySkipReason] = useState<string | null>(null);
  const [payingPurchaseId, setPayingPurchaseId] = useState<string | null>(null);
  const [autopayingPurchaseId, setAutopayingPurchaseId] = useState<string | null>(null);
  const [tryAutopayPurchaseId, setTryAutopayPurchaseId] = useState<string | null>(null);
  const [markDeliveredPurchase, setMarkDeliveredPurchase] = useState<OwnerPurchaseWithDetails | null>(null);
  const [markDeliveredProofFile, setMarkDeliveredProofFile] = useState<File | null>(null);
  const [defaultingPaymentMethodId, setDefaultingPaymentMethodId] = useState<string | null>(null);
  const [receiptImage, setReceiptImage] = useState<string | null>(null);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [periodStartOpen, setPeriodStartOpen] = useState(false);
  const [periodEndOpen, setPeriodEndOpen] = useState(false);
  type HistoryFilter = "all" | "pending" | "paid" | "auto_paid" | "rejected" | "ordered" | "delivered";
  const [historyFilter, setHistoryFilter] = useState<HistoryFilter>("all");
  const [activityTab, setActivityTab] = useState<"needs-review" | "history" | "invoices">("needs-review");

  const { data: invoices = [], isLoading: invoicesLoading } = useInvoicesByProperty(propertyId);
  const createFromPeriod = useCreateInvoiceFromPeriod();
  const { data: payeeConnectStatus } = usePayeeConnectStatus(
    agreement?.payee_type as "cleaner" | "company" | undefined,
    agreement?.payee_id ?? undefined
  );

  const billingMode = (property as { billing_mode?: string } | null)?.billing_mode ?? "solo";
  const isTrackOnly = billingMode === "track_only" || billingMode === "self_managed";
  const billingEnabled = billingMode !== "track_only" && billingMode !== "self_managed";
  const reimbursementMode = property ? ((property as { inventory_reimbursement_mode?: string })?.inventory_reimbursement_mode || "auto_pay") : "auto_pay";
  const inventoryAutopayEnabled =
    billingSettings?.inventory_autopay_enabled ?? (property as { inventory_autopay_enabled?: boolean })?.inventory_autopay_enabled !== false;

  const [defaultRateDollars, setDefaultRateDollars] = useState("");
  const defaultRateDollarsRef = useRef("");
  const rateInputDirtyRef = useRef(false);
  defaultRateDollarsRef.current = defaultRateDollars;
  useEffect(() => {
    if (rateInputDirtyRef.current) return;
    if (agreement?.default_rate_cents != null) {
      setDefaultRateDollars((agreement.default_rate_cents / 100).toFixed(2));
    } else {
      setDefaultRateDollars("");
    }
  }, [agreement?.default_rate_cents]);

  const paymentMethodsList: BillingProfilePaymentMethod[] =
    billingProfile?.payment_methods && billingProfile.payment_methods.length > 0
      ? billingProfile.payment_methods
      : billingProfile?.default_payment_method_id
        ? [{
            id: "",
            stripe_payment_method_id: billingProfile.default_payment_method_id,
            card_brand: billingProfile.card_brand ?? null,
            card_last4: billingProfile.card_last4 ?? null,
            card_exp_month: billingProfile.card_exp_month ?? null,
            card_exp_year: billingProfile.card_exp_year ?? null,
            is_default: true,
          }]
        : [];
  const hasCardOnFile = paymentMethodsList.length > 0 || !!cardInfo;
  const cardBrand = billingProfile?.card_brand ?? cardInfo?.brand ?? null;
  const cardLast4 = billingProfile?.card_last4 ?? cardInfo?.last4 ?? null;
  const cardExpMonth = billingProfile?.card_exp_month ?? cardInfo?.expMonth ?? null;
  const cardExpYear = billingProfile?.card_exp_year ?? cardInfo?.expYear ?? null;
  const currency = (agreement as { currency?: string })?.currency ?? "CAD";

  const handleSetupPaymentMethod = async () => {
    try {
      const res = await createSetupIntent.mutateAsync({ stripe_mode: STRIPE_PUBLISHABLE_KEY.startsWith("pk_test_") ? "test" : "live" });
      const { client_secret, setup_intent_id, stripe_mode } = res;
      if (!client_secret || !setup_intent_id) throw new Error("Missing client_secret or setup_intent_id from server");
      const mode = stripe_mode === "live" ? "live" : "test";
      const hasKey = mode === "test" ? !!STRIPE_PUBLISHABLE_KEY_TEST : !!STRIPE_PUBLISHABLE_KEY_LIVE;
      if (!hasKey) {
        toast.error(
          mode === "test"
            ? "Set VITE_STRIPE_TEST_PUBLISHABLE_KEY (pk_test_...) for sandbox card setup."
            : "Set VITE_STRIPE_PUBLISHABLE_KEY (pk_live_...) for live card setup."
        );
        return;
      }
      setCardSetupData({ clientSecret: client_secret, setupIntentId: setup_intent_id, stripe_mode: mode });
      setShowCardModal(true);
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : "Failed to initialize card setup");
    }
  };

  const handleCardSuccess = (result: { card_brand: string; card_last4: string; card_exp_month?: number; card_exp_year?: number }) => {
    setCardInfo({
      brand: result.card_brand,
      last4: result.card_last4,
      ...(result.card_exp_month != null && { expMonth: result.card_exp_month }),
      ...(result.card_exp_year != null && { expYear: result.card_exp_year }),
    });
    setShowCardModal(false);
    setCardSetupData(null);
    toast.success("Payment method added successfully");
  };

  const handleStatusChange = async (purchaseId: string, newStatus: "approved" | "rejected" | "paid") => {
    try {
      await updateStatus.mutateAsync({
        purchaseId,
        status: newStatus,
        paidAt: newStatus === "paid" ? new Date().toISOString() : null,
      });
      toast.success(`Purchase ${newStatus}`);
      setSelectedPurchase(null);
    } catch {
      toast.error("Failed to update status");
    }
  };

  const handleApproveAndPay = async (purchaseId: string) => {
    setPaymentError(null);
    setAutopaySkipReason(null);
    setPayingPurchaseId(purchaseId);
    try {
      await approveAndPay.mutateAsync(purchaseId);
      toast.success("Payment initiated successfully");
      setSelectedPurchase(null);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : "Payment failed";
      setPaymentError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setPayingPurchaseId(null);
    }
  };

  const handleTryAutopay = async (purchaseId: string) => {
    setAutopaySkipReason(null);
    setPaymentError(null);
    setTryAutopayPurchaseId(purchaseId);
    try {
      const result = await tryAutopay.mutateAsync(purchaseId);
      if (result.charged) {
        toast.success("Autopay succeeded");
        setSelectedPurchase(null);
      } else {
        setAutopaySkipReason(result.reason ?? "Autopay skipped");
        toast.error(result.reason ?? "Autopay skipped");
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Try autopay failed";
      setAutopaySkipReason(msg);
      toast.error(msg);
    } finally {
      setTryAutopayPurchaseId(null);
    }
  };

  const handleMarkAutopay = async (purchaseId: string) => {
    setAutopayingPurchaseId(purchaseId);
    try {
      await markAutopay.mutateAsync(purchaseId);
      toast.success("Marked as paid (Autopay). Will appear on invoice as $0.00 for recordkeeping.");
      setSelectedPurchase(null);
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Failed to mark as autopay");
    } finally {
      setAutopayingPurchaseId(null);
    }
  };

  const handleMarkDeliveredSubmit = async () => {
    if (!markDeliveredPurchase || !markDeliveredProofFile || !propertyId) return;
    const file = markDeliveredProofFile;
    const fileExt = file.name.split(".").pop();
    const fileName = `${propertyId}/delivered/${markDeliveredPurchase.id}/${Date.now()}.${fileExt}`;
    const { error: uploadErr } = await supabase.storage.from("cleaning-photos").upload(fileName, file);
    if (uploadErr) {
      toast.error("Failed to upload proof photo");
      return;
    }
    const { data: urlData } = supabase.storage.from("cleaning-photos").getPublicUrl(fileName);
    const proofUrl = urlData.publicUrl;
    try {
      await markDelivered.mutateAsync({
        purchaseId: markDeliveredPurchase.id,
        proofUrl,
        propertyId,
      });
      toast.success("Marked as delivered");
      setMarkDeliveredPurchase(null);
      setMarkDeliveredProofFile(null);
      setSelectedPurchase(null);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Failed to mark as delivered");
    }
  };

  const displayAmount = (p: OwnerPurchaseWithDetails) =>
    (p as { autopay_at?: string | null }).autopay_at ? 0 : p.amount;

  const displayPurchases = (purchases && Array.isArray(purchases)) ? purchases : [];
  // Map legacy inventory_reimbursements to a shape compatible with the list (id prefixed so no collision)
  const irAsDisplay = (legacyReimbursements ?? []).map((r) => ({
    id: `ir-${r.id}`,
    source: "inventory_reimbursement" as const,
    property_id: r.property_id,
    item_name: r.description || "Reimbursement",
    quantity: 1,
    amount: r.amount_cents / 100,
    status: r.status === "submitted" || r.status === "pending_approval" ? "pending_approval" : r.status === "paid" ? "paid" : r.status === "rejected" ? "rejected" : "approved",
    type: "cleaner_purchase" as const,
    created_at: r.created_at,
    receipt_url: Array.isArray(r.receipt_urls) && r.receipt_urls[0] ? r.receipt_urls[0] : null,
    proof_url: null,
    paid_at: r.paid_at,
    payment_failed: false,
    delivery_status: "delivered" as const,
    cleaner_name: null,
    autopay_at: r.paid_method === "autopay" ? r.paid_at : null,
    _irId: r.id,
  }));
  type DisplayItem = OwnerPurchaseWithDetails & { source?: "cleaner_purchase"; _irId?: string };
  const mergedDisplay: (DisplayItem | (typeof irAsDisplay)[0])[] = [
    ...displayPurchases.map((p) => ({ ...p, source: "cleaner_purchase" as const })),
    ...irAsDisplay,
  ];
  const allPurchasesHistory = [...mergedDisplay].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );
  const filteredHistory = (() => {
    if (historyFilter === "all") return allPurchasesHistory;
    return allPurchasesHistory.filter((p) => {
      const autopay = !!(p as { autopay_at?: string }).autopay_at;
      const deliveryStatus = (p as { delivery_status?: string }).delivery_status;
      switch (historyFilter) {
        case "pending":
          return p.status === "pending_approval";
        case "paid":
          return (p.status === "paid" || p.status === "approved") && !autopay && deliveryStatus !== "ordered";
        case "auto_paid":
          return autopay;
        case "rejected":
          return p.status === "rejected";
        case "ordered":
          return deliveryStatus === "ordered";
        case "delivered":
          return deliveryStatus === "delivered";
        default:
          return true;
      }
    });
  })();
  // Needs review: cleaner purchases needing action + request_order until resolved + legacy IR pending
  const needsReviewItems = mergedDisplay.filter((p) => {
    if ("_irId" in p && p._irId) {
      return p.status === "pending_approval";
    }
    if (p.type === "cleaner_purchase") {
      return p.status === "pending_approval" || (p.status === "approved" && (p as OwnerPurchaseWithDetails).payment_failed === true);
    }
    if (p.type === "request_order") {
      return p.status === "pending_approval" || (p.status === "approved" && (p as OwnerPurchaseWithDetails).delivery_status === "ordered");
    }
    return false;
  });
  const needsReviewCount = needsReviewItems.length;

  const handleSelectItem = (item: (typeof mergedDisplay)[number]) => {
    if ("_irId" in item && item._irId) {
      setSelectedReimbursement(legacyReimbursements.find((r) => r.id === item._irId) ?? null);
      setSelectedPurchase(null);
    } else {
      setSelectedPurchase(item as OwnerPurchaseWithDetails);
      setSelectedReimbursement(null);
      setPaymentError(null);
      setAutopaySkipReason(null);
    }
  };

  const getStatusBadge = (status: string, paymentFailed?: boolean | null, isAutoPay?: boolean) => {
    if (status === "pending_approval") {
      if (paymentFailed && isAutoPay) {
        return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Auto-pay failed</Badge>;
      }
      if (isAutoPay && !paymentFailed) {
        return (
          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
            Auto-paying...
          </Badge>
        );
      }
      return <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">Pending</Badge>;
    }
    if (paymentFailed && status === "approved") {
      return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Payment Failed</Badge>;
    }
    switch (status) {
      case "approved":
        return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Approved</Badge>;
      case "auto_approved":
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Paid</Badge>;
      case "paid":
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Paid</Badge>;
      case "rejected":
        return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getTypeBadge = (type: string) => {
    if (type === "request_order") {
      return <Badge variant="secondary" className="text-xs"><Package className="w-3 h-3 mr-1" />Request</Badge>;
    }
    return <Badge variant="secondary" className="text-xs"><Receipt className="w-3 h-3 mr-1" />Purchase</Badge>;
  };

  const handleGenerateInvoice = async () => {
    if (!periodStart || !periodEnd) {
      toastHook({ title: "Select date range", variant: "destructive" });
      return;
    }
    const start = new Date(periodStart);
    const end = new Date(periodEnd);
    if (start > end) {
      toastHook({ title: "Start date must be before end date", variant: "destructive" });
      return;
    }
    try {
      const ids = await createFromPeriod.mutateAsync({
        propertyId,
        periodStart,
        periodEnd,
      });
      toastHook({ title: ids.length > 0 ? `Created ${ids.length} invoice(s)` : "No invoiceable items in this period" });
      if (ids.length > 0) {
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.set("tab", "payments");
          next.set("invoiceId", ids[0]);
          return next;
        });
        const baseUrl = getBaseUrl().replace(/\/$/, "");
        const name = propertyName ?? "Property";
        let emailFailed = false;
        for (const id of ids) {
          const { data: inv } = await supabase.from("invoices").select("period_start, period_end, total_amount_cents").eq("id", id).single();
          const { data: items = [] } = await supabase.from("invoice_items").select("item_type").eq("invoice_id", id).then((r) => r.data ?? []);
          const cleaningCount = (items as { item_type: string }[]).filter((i) => i.item_type === "cleaning").length;
          const reimbursementsCount = (items as { item_type: string }[]).filter((i) => i.item_type === "inventory_reimbursement" || i.item_type === "cleaner_purchase").length;
          const viewInvoiceUrl = `${baseUrl}/properties/${propertyId}?tab=payments&invoiceId=${id}`;
          const totalDueCents = (inv as { total_amount_cents?: number })?.total_amount_cents ?? 0;
          const payload = {
            propertyName: name,
            periodStart,
            periodEnd,
            totalDueCents,
            cleaningCount,
            reimbursementsCount,
            viewInvoiceUrl,
          };
          const recipients = await getInvoiceRecipientEmails(id);
          const emailsToSend = recipients
            ? [...new Set([recipients.owner_email, recipients.payee_email].filter(Boolean) as string[])]
            : user?.email?.trim().toLowerCase() ? [user.email.trim().toLowerCase()] : [];
          for (const recipientEmail of emailsToSend) {
            if (!recipientEmail) continue;
            const recipientName = recipientEmail === user?.email?.trim().toLowerCase() ? (profile?.full_name ?? null) : null;
            const result = await sendInvoiceReadyEmail({
              recipientEmail,
              recipientName: recipientName ?? undefined,
              ...payload,
            });
            if (!result.ok) emailFailed = true;
          }
        }
        if (emailFailed) toastHook({ title: "Invoice(s) created; email notification failed", variant: "destructive" });
      }
    } catch (e) {
      toastHook({ title: "Failed to generate invoice", description: (e as Error).message, variant: "destructive" });
    }
  };

  const handleInvoiceBack = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("invoiceId");
      return next;
    });
  };

  // Invoice detail view (Section D full-screen when invoiceId set)
  if (invoiceId) {
    return (
      <InvoiceDetailView
        invoiceId={invoiceId}
        propertyName={propertyName}
        onBack={handleInvoiceBack}
        canManagePaymentsOwnerOnly={canManagePaymentMethodAndToggles}
      />
    );
  }

  const goToTeamAndAccess = () => {
    navigate(`/properties/${propertyId}?tab=settings#access-and-team`);
  };

  return (
    <div className="space-y-4 md:space-y-6 min-w-0">
      {!isPropertyOwner && (
        <Alert className="border-amber-200 bg-amber-50/80 dark:border-amber-900 dark:bg-amber-950/30">
          <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-500" />
          <AlertDescription>
            {isReadOnlyPayments ? (
              <>You're viewing Payments as a <strong>team member (Limited access)</strong>. You can see relationship type, payee, rates, invoice frequency, tax settings, Pay on Stravo status, invoices, and inventory spend. You cannot edit any settings or approve/pay. Only the owner can manage payment methods and reimbursement mode.</>
            ) : (
              <>You're viewing Payments as a <strong>team member (Full access)</strong>. You can edit the default rate and per-task rate overrides, and view relationship type, payee, invoice frequency, tax settings, and Pay on Stravo status. You can view invoices and inventory spend, and use Mark delivered / add notes. You cannot change Pay on Stravo, payment method, reimbursement mode, or approve/pay invoices. Only the owner can manage those.</>
            )}
          </AlertDescription>
        </Alert>
      )}

      {/* Section A — Payments setup */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Payments setup</CardTitle>
          <CardDescription className="text-sm">
            Choose who gets paid, how often invoices are created, and whether Stravo pays in-app.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Relationship type</Label>
            <Select
              value={billingMode === "company" ? "solo" : billingMode}
              onValueChange={async (v: "solo" | "track_only") => {
                if (!canManagePaymentMethodAndToggles) return;
                const mode = v === "track_only" ? "track_only" : "solo";
                try {
                  await updateProperty.mutateAsync({ propertyId, billing_mode: mode });
                  toast.success("Updated");
                } catch {
                  toast.error("Failed to update");
                }
              }}
              disabled={!canManagePaymentMethodAndToggles}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="solo">Pay team member</SelectItem>
                <SelectItem value="track_only">Track only — Invoices are record keeping only (no Pay button)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {billingMode !== "track_only" && billingMode !== "self_managed" && (
            <div className="space-y-2">
              <Label>Select who gets paid</Label>
              {(() => {
                const payeeOptions: { value: string; label: string; payeeType: "cleaner" | "company" }[] = [
                  ...cleanersForPayee.map((m) => ({ value: `cleaner:${m.value}`, label: `${m.name} (Cleaner)`, payeeType: "cleaner" as const })),
                  ...companiesForPayee.map((c) => ({ value: `company:${c.id}`, label: `${c.company_name ?? "Company"} (Team)`, payeeType: "company" as const })),
                ];
                const currentValue = agreement?.payee_type && agreement?.payee_id ? `${agreement.payee_type}:${agreement.payee_id}` : "";
                if (payeeOptions.length === 0) {
                  return (
                    <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
                      <p>No cleaners or team members added. Invite people in Team & Access.</p>
                      <Button variant="outline" size="sm" className="mt-2" onClick={goToTeamAndAccess}>
                        Go to Team & Access
                      </Button>
                    </div>
                  );
                }
                return (
                  <Select
                    value={currentValue}
                    onValueChange={async (raw: string) => {
                      if (!canManagePaymentMethodAndToggles) return;
                      const [payeeType, payeeId] = raw.split(":");
                      if (!payeeType || !payeeId || (payeeType !== "cleaner" && payeeType !== "company")) return;
                      try {
                        await upsertAgreement.mutateAsync({
                          payee_type: payeeType,
                          payee_id: payeeId,
                          default_rate_cents: agreement?.default_rate_cents ?? 0,
                          currency: (agreement as { currency?: string })?.currency ?? "CAD",
                          payment_mode: (agreement?.payment_mode as PaymentMode) ?? "off_app",
                          payout_schedule: (agreement?.payout_schedule as PayoutSchedule) ?? "monthly",
                          inventory_included: true,
                          inventory_approval_required: false,
                        });
                        toast.success("Payee updated");
                      } catch {
                        toast.error("Failed to update payee");
                      }
                    }}
                    disabled={upsertAgreement.isPending || !canManagePaymentMethodAndToggles}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select cleaner or team member" />
                    </SelectTrigger>
                    <SelectContent>
                      {payeeOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                );
              })()}
            </div>
          )}
          {billingEnabled && (
            <>
              <div className="space-y-2">
                <Label>Default rate &amp; currency (for invoices)</Label>
                <p className="text-xs text-muted-foreground">Used when a task doesn&apos;t have a custom per-cleaning rate.</p>
                <div className="flex flex-wrap gap-2 items-center">
                  <Select
                    value={(agreement as { currency?: string })?.currency ?? "CAD"}
                    onValueChange={async (v: AgreementCurrency) => {
                      if (!canEditRates) return;
                      const payee = agreement?.payee_id && agreement?.payee_type
                        ? { payee_type: agreement.payee_type, payee_id: agreement.payee_id }
                        : null;
                      if (!payee) {
                        toast.error("Select a payee above first.");
                        return;
                      }
                      try {
                        await upsertAgreement.mutateAsync({
                          payee_type: payee.payee_type,
                          payee_id: payee.payee_id,
                          default_rate_cents: agreement?.default_rate_cents ?? Math.round(parseFloat(defaultRateDollars || "0") * 100) ?? 0,
                          currency: v,
                          payment_mode: (agreement?.payment_mode as PaymentMode) ?? "off_app",
                          payout_schedule: (agreement?.payout_schedule as PayoutSchedule) ?? "monthly",
                          inventory_included: true,
                          inventory_approval_required: false,
                        });
                        toast.success("Currency updated");
                      } catch {
                        toast.error("Failed to update currency");
                      }
                    }}
                    disabled={upsertAgreement.isPending || !canEditRates}
                  >
                    <SelectTrigger className="w-[100px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CAD">CAD</SelectItem>
                      <SelectItem value="USD">USD</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    min={0}
                    step={0.01}
                    placeholder="0.00"
                    value={defaultRateDollars}
                    onChange={(e) => {
                      if (!canEditRates) return;
                      rateInputDirtyRef.current = true;
                      setDefaultRateDollars(e.target.value);
                    }}
                    onBlur={async () => {
                      if (!canEditRates) return;
                      const cents = Math.max(0, Math.round(parseFloat(defaultRateDollars || "0") * 100));
                      const payee = agreement?.payee_id && agreement?.payee_type
                        ? { payee_type: agreement.payee_type, payee_id: agreement.payee_id }
                        : null;
                      if (!payee) return;
                      try {
                        await upsertAgreement.mutateAsync({
                          payee_type: payee.payee_type,
                          payee_id: payee.payee_id,
                          default_rate_cents: cents,
                          currency: (agreement as { currency?: string })?.currency ?? "CAD",
                          payment_mode: (agreement?.payment_mode as PaymentMode) ?? "off_app",
                          payout_schedule: (agreement?.payout_schedule as PayoutSchedule) ?? "monthly",
                          inventory_included: true,
                          inventory_approval_required: false,
                        });
                        rateInputDirtyRef.current = false;
                        toast.success("Default rate saved");
                      } catch {
                        toast.error("Failed to save rate");
                      }
                    }}
                    disabled={upsertAgreement.isPending || !canEditRates}
                    className="w-28"
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    disabled={upsertAgreement.isPending || !canEditRates}
                    onClick={async () => {
                      if (!canEditRates) return;
                      const cents = Math.max(0, Math.round(parseFloat(defaultRateDollars || "0") * 100));
                      const payee = agreement?.payee_id && agreement?.payee_type
                        ? { payee_type: agreement.payee_type, payee_id: agreement.payee_id }
                        : null;
                      if (!payee) {
                        toast.error("Select a payee above first.");
                        return;
                      }
                      try {
                        await upsertAgreement.mutateAsync({
                          payee_type: payee.payee_type,
                          payee_id: payee.payee_id,
                          default_rate_cents: cents,
                          currency: (agreement as { currency?: string })?.currency ?? "CAD",
                          payment_mode: (agreement?.payment_mode as PaymentMode) ?? "off_app",
                          payout_schedule: (agreement?.payout_schedule as PayoutSchedule) ?? "monthly",
                          inventory_included: true,
                          inventory_approval_required: false,
                        });
                        rateInputDirtyRef.current = false;
                        toast.success("Default rate saved");
                      } catch {
                        toast.error("Failed to save rate");
                      }
                    }}
                  >
                    {upsertAgreement.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
                  </Button>
                </div>
                {!agreement?.payee_id && (
                  <p className="text-xs text-amber-600">Select a payee above to save rate and currency.</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Invoice frequency</Label>
                <p className="text-xs text-muted-foreground">This controls how cleanings + inventory are grouped onto invoices.</p>
                <Select
                  value={billingSettings?.invoice_frequency ?? "monthly"}
                  onValueChange={async (v: InvoiceFrequency) => {
                    if (!canManagePaymentMethodAndToggles) return;
                    try {
                      await updateBillingPreferences.mutateAsync({ invoice_frequency: v });
                      if (agreement) {
                        await upsertAgreement.mutateAsync({
                          payee_type: agreement.payee_type,
                          payee_id: agreement.payee_id,
                          default_rate_cents: agreement.default_rate_cents ?? 0,
                          currency: (agreement as { currency?: string })?.currency ?? "CAD",
                          payment_mode: (agreement.payment_mode as PaymentMode) ?? "off_app",
                          payout_schedule: (v as PayoutSchedule) ?? "monthly",
                          inventory_included: true,
                          inventory_approval_required: false,
                        });
                      }
                      toast.success("Updated");
                    } catch {
                      toast.error("Failed to update");
                    }
                  }}
                  disabled={updateBillingPreferences.isPending || !canManagePaymentMethodAndToggles}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Frequency" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="per_cleaning">Per cleaning</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="biweekly">Bi-weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 rounded-lg border p-3 bg-muted/30">
                <Label className="text-sm font-medium">Tax on cleanings</Label>
                <p className="text-xs text-muted-foreground">
                  Tax is applied to cleaning fees only (v1). Inventory reimbursements are pass-through.
                </p>
                <div className="flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-sm font-medium">Charge tax on cleanings</p>
                  </div>
                  <Switch
                    checked={billingSettings?.cleaning_tax_enabled === true}
                    onCheckedChange={async (checked) => {
                      if (!canManagePaymentMethodAndToggles) return;
                      try {
                        await updateBillingPreferences.mutateAsync({
                          cleaning_tax_enabled: checked,
                        });
                        toast.success(checked ? "Tax enabled" : "Tax disabled");
                      } catch {
                        toast.error("Failed to update tax");
                      }
                    }}
                    disabled={updateBillingPreferences.isPending || !canManagePaymentMethodAndToggles}
                  />
                </div>
                {billingSettings?.cleaning_tax_enabled === true && (
                  <div className="flex flex-wrap gap-3 pt-2">
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Tax rate %</Label>
                      <Input
                        type="number"
                        min={0}
                        step={0.01}
                        placeholder="0"
                        value={
                          billingSettings?.cleaning_tax_rate_percent != null
                            ? String(billingSettings.cleaning_tax_rate_percent)
                            : ""
                        }
                        onChange={async (e) => {
                          if (!canManagePaymentMethodAndToggles) return;
                          const raw = e.target.value;
                          const n = raw === "" ? null : Number(raw);
                          if (raw !== "" && !Number.isFinite(n)) return;
                          try {
                            await updateBillingPreferences.mutateAsync({
                              cleaning_tax_rate_percent: n,
                            });
                          } catch {
                            // keep silent while typing; error will show on blur/save UX later if needed
                          }
                        }}
                        disabled={!canManagePaymentMethodAndToggles}
                        className="w-28"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Tax label</Label>
                      <Input
                        type="text"
                        placeholder="Tax"
                        value={billingSettings?.cleaning_tax_label ?? "Tax"}
                        onChange={async (e) => {
                          if (!canManagePaymentMethodAndToggles) return;
                          const v = e.target.value;
                          try {
                            await updateBillingPreferences.mutateAsync({
                              cleaning_tax_label: v,
                            });
                          } catch {
                            // keep silent while typing
                          }
                        }}
                        disabled={!canManagePaymentMethodAndToggles}
                        className="w-44"
                      />
                    </div>
                  </div>
                )}
              </div>
              {canManagePaymentMethodAndToggles ? (
                <div className="flex items-center justify-between gap-4 rounded-lg border p-3">
                  <div className="min-w-0">
                    <p className="font-medium text-sm">Pay on Stravo</p>
                    <p className="text-xs text-muted-foreground">When on, owners pay in-app via card. When off, invoices are for record-keeping.</p>
                  </div>
                  <Switch
                    checked={billingSettings?.pay_via_app !== false}
                    onCheckedChange={async (checked) => {
                      try {
                        await updateBillingPreferences.mutateAsync({ pay_via_app: checked });
                        if (agreement) {
                          await upsertAgreement.mutateAsync({
                            payee_type: agreement.payee_type,
                            payee_id: agreement.payee_id,
                            default_rate_cents: agreement.default_rate_cents ?? 0,
                            currency: (agreement as { currency?: string })?.currency ?? "CAD",
                            payment_mode: (checked ? "on_app" : "off_app") as PaymentMode,
                            payout_schedule: (agreement.payout_schedule as PayoutSchedule) ?? "monthly",
                            inventory_included: true,
                            inventory_approval_required: false,
                          });
                        }
                        toast.success(checked ? "Pay on Stravo enabled" : "Pay off-app");
                      } catch {
                        toast.error("Failed to update");
                      }
                    }}
                    disabled={updateBillingPreferences.isPending}
                  />
                </div>
              ) : (
                <div className="flex items-start gap-3 rounded-lg border p-3 bg-muted/30 border-amber-200 dark:border-amber-900">
                  <Lock className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <p className="font-medium text-sm">Pay on Stravo</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {billingSettings?.pay_via_app !== false ? "On — owners pay in-app via card." : "Off — invoices are for record-keeping."}
                    </p>
                    <p className="text-xs text-amber-600 dark:text-amber-500 mt-2">Only the owner can manage this.</p>
                  </div>
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                Owners cover processing fees so payees receive the full amount owed.
              </p>
            </>
          )}
          {billingEnabled && billingSettings?.pay_via_app === false && (
            <p className="text-sm text-muted-foreground pt-1">
              Pay is handled off-app. Invoices are for record-keeping.
            </p>
          )}
        </CardContent>
      </Card>

      {!isTrackOnly && (
      <>
      {/* 2) Paying — Payment method + Payee payout readiness (when Pay on Stravo) */}
      {billingSettings?.pay_via_app !== false && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <CreditCard className="w-4 h-4" />
              Paying
            </CardTitle>
            <CardDescription className="text-sm">
              Payment method for invoices and reimbursements. Payee must have payouts connected to receive payment in Stravo.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!canManagePaymentMethodAndToggles ? (
              <div className="flex items-start gap-3 rounded-lg border p-3 bg-muted/30 border-amber-200 dark:border-amber-900">
                <Lock className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="font-medium text-sm">Payment method</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {hasCardOnFile && (cardBrand || cardLast4)
                      ? `${cardBrand ? cardBrand.charAt(0).toUpperCase() + cardBrand.slice(1) : "Card"} •••• ${cardLast4 ?? "—"} on file.`
                      : "No payment method on file."}
                  </p>
                  <p className="text-xs text-amber-600 dark:text-amber-500 mt-2">Only the owner can manage this.</p>
                </div>
              </div>
            ) : (
            <>
            <div>
              <p className="text-sm font-medium mb-2">Payment method</p>
              {billingLoading ? (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">Loading…</span>
                </div>
              ) : billingError ? (
                <p className="text-sm text-destructive">{billingError.message}</p>
              ) : (
                <div className="flex flex-wrap items-center gap-3">
                  {paymentMethodsList.length > 0 ? (
                    <>
                      <span className="text-sm">
                        {cardBrand && cardLast4
                          ? `${cardBrand.charAt(0).toUpperCase() + cardBrand.slice(1)} •••• ${cardLast4}`
                          : "Card on file"}
                        {cardExpMonth != null && cardExpYear != null && (
                          <span className="text-muted-foreground ml-1">
                            Exp {String(cardExpMonth).padStart(2, "0")}/{String(cardExpYear).slice(-2)}
                          </span>
                        )}
                      </span>
                      <Button variant="outline" size="sm" onClick={handleSetupPaymentMethod}>
                        Update card
                      </Button>
                    </>
                  ) : (
                    <Button size="sm" onClick={handleSetupPaymentMethod}>
                      <CreditCard className="w-4 h-4 mr-2" />
                      Add payment method
                    </Button>
                  )}
                </div>
              )}
            </div>
            <div>
              <p className="text-sm font-medium mb-2">Payee readiness</p>
              {!agreement?.payee_id ? (
                <p className="text-sm text-amber-600">Select a payee above.</p>
              ) : payeeConnectStatus?.stripe_connect_onboarding_status === "complete" && payeeConnectStatus?.stripe_connect_payouts_enabled ? (
                <p className="text-sm text-muted-foreground">Ready to pay in Stravo.</p>
              ) : (
                <>
                  <p className="text-sm text-amber-600">Payee hasn&apos;t connected payouts yet.</p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    onClick={() => {
                      const msg = "Please connect your payouts in Account → Payouts so you can receive payments in Stravo.";
                      navigator.clipboard.writeText(msg);
                      toast.success("Message copied to clipboard");
                    }}
                  >
                    Copy message to payee
                  </Button>
                </>
              )}
            </div>
            </>
            )}
          </CardContent>
        </Card>
      )}
      </>
      )}

      {/* Section C — Activity */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Activity</CardTitle>
          <CardDescription className="text-sm">
            Inventory spend and invoices for this property.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Tabs value={activityTab} onValueChange={(v) => setActivityTab(v as "needs-review" | "history" | "invoices")} className="w-full">
            <div className="overflow-x-auto pb-1 -mb-1 mx-4 md:mx-6">
            <TabsList className="w-max min-w-full justify-start rounded-none border-b bg-transparent p-0 h-9 flex-nowrap gap-0">
              <TabsTrigger value="needs-review" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:shadow-none whitespace-nowrap shrink-0">
                Needs review {needsReviewCount > 0 && `(${needsReviewCount})`}
              </TabsTrigger>
              <TabsTrigger value="history" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:shadow-none whitespace-nowrap shrink-0">
                History
              </TabsTrigger>
              <TabsTrigger value="invoices" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:shadow-none whitespace-nowrap shrink-0">
                Invoices
              </TabsTrigger>
            </TabsList>
            </div>
            <TabsContent value="needs-review" className="mt-0 p-4 md:px-6 space-y-4">
              {canManagePaymentMethodAndToggles ? (
                <div className="space-y-2 rounded-lg border p-3 bg-muted/30">
                  <Label className="text-sm font-medium">Inventory reimbursement mode</Label>
                  <Select
                    value={reimbursementMode}
                    onValueChange={async (v: "auto_pay" | "require_approval" | "disabled") => {
                      try {
                        await updateProperty.mutateAsync({ propertyId, inventory_reimbursement_mode: v });
                        // Optimistically update cache so dropdown reflects new value immediately
                        queryClient.setQueryData(
                          ["property", propertyId, user?.id],
                          (prev: unknown) =>
                            prev && typeof prev === "object"
                              ? { ...(prev as Record<string, unknown>), inventory_reimbursement_mode: v }
                              : prev
                        );
                        queryClient.setQueryData(
                          ["property-with-access", propertyId, user?.id, profile?.role],
                          (prev: unknown) =>
                            prev && typeof prev === "object"
                              ? { ...(prev as Record<string, unknown>), inventory_reimbursement_mode: v }
                              : prev
                        );
                        toast.success("Updated");
                      } catch {
                        toast.error("Failed to update");
                      }
                    }}
                    disabled={updateProperty.isPending}
                  >
                    <SelectTrigger className="w-full max-w-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto_pay">Auto-pay</SelectItem>
                      <SelectItem value="require_approval">Require approval</SelectItem>
                      <SelectItem value="disabled">Disabled</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {reimbursementMode === "auto_pay" && "Automatically reimburses cleaners after receipt + proof are uploaded."}
                    {reimbursementMode === "require_approval" && "You review each reimbursement before it's paid."}
                    {reimbursementMode === "disabled" && "Cleaners can't submit reimbursements. Requests to order still allowed."}
                  </p>
                </div>
              ) : (
                <div className="flex items-start gap-3 rounded-lg border p-3 bg-muted/30 border-amber-200 dark:border-amber-900">
                  <Lock className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <p className="font-medium text-sm">Inventory reimbursement mode</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {reimbursementMode === "auto_pay" && "Auto-pay — automatically reimburses after receipt + proof."}
                      {reimbursementMode === "require_approval" && "Require approval — owner reviews each reimbursement before payment."}
                      {reimbursementMode === "disabled" && "Disabled — cleaners can't submit reimbursements."}
                    </p>
                    <p className="text-xs text-amber-600 dark:text-amber-500 mt-2">Only the owner can manage this.</p>
                  </div>
                </div>
              )}
              <p className="text-xs text-muted-foreground">Updated just now</p>
              {purchasesLoading || reimbursementsLoading ? (
                <p className="text-sm text-muted-foreground">Loading…</p>
              ) : needsReviewItems.length === 0 ? (
                <p className="text-sm text-muted-foreground">No pending purchases or order requests.</p>
              ) : (
                <div className="space-y-2">
                  {needsReviewItems.map((p) => (
                    <div
                      key={p.id}
                      className="flex items-center justify-between gap-2 p-3 rounded-lg border cursor-pointer hover:bg-muted/50"
                      onClick={() => handleSelectItem(p)}
                    >
                      <div className="min-w-0">
                        <span className="font-medium text-sm">{p.item_name || (p.type === "request_order" ? "Order request" : "—")}</span>
                        {p.type === "cleaner_purchase" && (
                          <span className="text-sm text-muted-foreground ml-2">${displayAmount(p).toFixed(2)}</span>
                        )}
                        <div className="text-xs text-muted-foreground">
                          {format(new Date(p.created_at), "MMM d")} · {p.cleaner_name || "—"}
                          {p.type === "request_order" && ` · Qty ${p.quantity}`}
                          {propertyName && ` · ${propertyName}`}
                        </div>
                      </div>
                      {p.type === "request_order" ? getTypeBadge("request_order") : getStatusBadge(p.status, p.payment_failed, reimbursementMode === "auto_pay")}
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
            <TabsContent value="history" className="mt-0 p-4 md:px-6 space-y-4">
              <div className="flex flex-wrap gap-2">
                {(["all", "pending", "paid", "auto_paid", "rejected", "ordered", "delivered"] as const).map((f) => (
                  <Button
                    key={f}
                    variant={historyFilter === f ? "secondary" : "ghost"}
                    size="sm"
                    className="h-8"
                    onClick={() => setHistoryFilter(f)}
                  >
                    {f === "all" ? "All" : f === "auto_paid" ? "Auto-paid" : f.charAt(0).toUpperCase() + f.slice(1)}
                  </Button>
                ))}
              </div>
              {purchasesLoading || reimbursementsLoading ? (
                <p className="text-sm text-muted-foreground">Loading…</p>
              ) : filteredHistory.length === 0 ? (
                <p className="text-sm text-muted-foreground">No matching items.</p>
              ) : (
                <div className="space-y-2">
                  {filteredHistory.map((p) => (
                    <div
                      key={p.id}
                      className="flex items-center justify-between gap-2 p-3 rounded-lg border cursor-pointer hover:bg-muted/50"
                      onClick={() => handleSelectItem(p)}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-sm">{p.item_name || (p.type === "request_order" ? "Order request" : "—")}</span>
                          <span className="text-sm text-muted-foreground">
                            Qty {p.quantity}
                            {" · "}
                            {displayAmount(p) > 0 ? `$${displayAmount(p).toFixed(2)}` : (p as { autopay_at?: string }).autopay_at ? "$0 (Autopay)" : "$0"}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap mt-0.5">
                          {format(new Date(p.created_at), "MMM d, yyyy HH:mm")}
                          {getTypeBadge(p.type)}
                          {p.cleaner_name && <span>{p.cleaner_name}</span>}
                          {propertyName && <span>{propertyName}</span>}
                        </div>
                        <div className="flex gap-2 mt-1">
                          {p.receipt_url && (
                            <a href={p.receipt_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline" onClick={(e) => e.stopPropagation()}>
                              Receipt
                            </a>
                          )}
                          {p.proof_url && (
                            <a href={p.proof_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline" onClick={(e) => e.stopPropagation()}>
                              Proof
                            </a>
                          )}
                        </div>
                      </div>
                      {getStatusBadge(p.status, p.payment_failed, reimbursementMode === "auto_pay")}
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
            <TabsContent value="invoices" className="mt-0 p-4 md:px-6 space-y-4">
              <div>
                <p className="text-sm font-medium mb-2">Past invoices</p>
                {invoicesLoading ? (
                  <p className="text-sm text-muted-foreground">Loading…</p>
                ) : invoices.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No invoices yet.</p>
                ) : (
                  <ul className="space-y-2">
                    {invoices.map((inv) => (
                      <li key={inv.id}>
                        <Button
                          variant="ghost"
                          className="w-full justify-between font-normal min-w-0"
                          onClick={() => {
                            setSearchParams((prev) => {
                              const next = new URLSearchParams(prev);
                              next.set("tab", "payments");
                              next.set("invoiceId", inv.id);
                              return next;
                            });
                          }}
                        >
                          <span className="truncate min-w-0 mr-2">
                            {format(parseISO(inv.period_start), "MMM d")} – {format(parseISO(inv.period_end), "MMM d, yyyy")}
                            {" · "}
                            Subtotal {formatMoney(Number((inv as any).subtotal_amount_cents ?? inv.total_amount_cents), currency)}
                          </span>
                          <span className="text-muted-foreground text-xs shrink-0">{inv.status}</span>
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Invoices are already created automatically based on invoice frequency. Use custom dates only when needed.
              </p>
              {canManagePaymentMethodAndToggles && (
                <Button variant="outline" className="mt-2" onClick={() => setGenerateOpen(true)}>
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  Generate invoice for custom dates
                </Button>
              )}
              <Dialog open={generateOpen} onOpenChange={setGenerateOpen}>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>Generate invoice for custom dates</DialogTitle>
                    <DialogDescription>Choose start and end date for the invoice period.</DialogDescription>
                  </DialogHeader>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-4">
                    <div className="space-y-2">
                      <Label>Period start</Label>
                      <Popover open={periodStartOpen} onOpenChange={setPeriodStartOpen}>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="w-full justify-start text-left font-normal">
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {periodStart ? format(parseISO(periodStart), "MMM d, yyyy") : "Pick start date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={periodStart ? parseISO(periodStart) : undefined}
                            onSelect={(date) => {
                              if (date) {
                                setPeriodStart(format(date, "yyyy-MM-dd"));
                                setPeriodStartOpen(false);
                              }
                            }}
                            className="p-3 pointer-events-auto"
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div className="space-y-2">
                      <Label>Period end</Label>
                      <Popover open={periodEndOpen} onOpenChange={setPeriodEndOpen}>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="w-full justify-start text-left font-normal">
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {periodEnd ? format(parseISO(periodEnd), "MMM d, yyyy") : "Pick end date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={periodEnd ? parseISO(periodEnd) : undefined}
                            onSelect={(date) => {
                              if (date) {
                                setPeriodEnd(format(date, "yyyy-MM-dd"));
                                setPeriodEndOpen(false);
                              }
                            }}
                            className="p-3 pointer-events-auto"
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setGenerateOpen(false)}>Cancel</Button>
                    <Button
                      onClick={async () => {
                        await handleGenerateInvoice();
                        setGenerateOpen(false);
                      }}
                      disabled={createFromPeriod.isPending || !periodStart || !periodEnd}
                    >
                      {createFromPeriod.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                      Generate
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Legacy inventory_reimbursement detail sheet (approve/reject) */}
      <Sheet open={!!selectedReimbursement} onOpenChange={(open) => !open && setSelectedReimbursement(null)}>
        <SheetContent className="w-[95vw] max-w-md max-h-[85vh] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Reimbursement details</SheetTitle>
          </SheetHeader>
          {selectedReimbursement && (
            <div className="mt-6 space-y-4">
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Description</span>
                  <span className="font-medium">{selectedReimbursement.description || "Reimbursement"}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Amount</span>
                  <span className="font-medium">{formatMoney(selectedReimbursement.amount_cents, currency)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Status</span>
                  <Badge variant="outline">{selectedReimbursement.status}</Badge>
                </div>
                {Array.isArray(selectedReimbursement.receipt_urls) && selectedReimbursement.receipt_urls[0] && (
                  <div className="pt-2 border-t">
                    <span className="text-sm text-muted-foreground">Receipt</span>
                    <a href={selectedReimbursement.receipt_urls[0]} target="_blank" rel="noopener noreferrer" className="block mt-2 text-primary text-sm hover:underline">
                      View receipt
                    </a>
                  </div>
                )}
              </div>
              {(selectedReimbursement.status === "submitted" || selectedReimbursement.status === "pending_approval") && (
                <div className="flex gap-2 pt-4 border-t">
                  {canPerformNonPaymentActions && (
                    <Button
                      variant="outline"
                      className="flex-1 text-red-600"
                      onClick={async () => {
                        try {
                          await rejectReimbursement.mutateAsync(selectedReimbursement.id);
                          toast.success("Rejected");
                          setSelectedReimbursement(null);
                        } catch {
                          toast.error("Failed to reject");
                        }
                      }}
                      disabled={rejectReimbursement.isPending}
                    >
                      <X className="w-4 h-4 mr-2" />
                      Reject
                    </Button>
                  )}
                  {canManagePaymentMethodAndToggles && (
                    <Button
                      className="flex-1"
                      onClick={async () => {
                        try {
                          await approveReimbursement.mutateAsync(selectedReimbursement.id);
                          toast.success("Approved");
                          setSelectedReimbursement(null);
                        } catch {
                          toast.error("Failed to approve");
                        }
                      }}
                      disabled={approveReimbursement.isPending}
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Approve
                    </Button>
                  )}
                  {!canPerformNonPaymentActions && !canManagePaymentMethodAndToggles && (
                    <p className="text-xs text-muted-foreground py-2">Only the owner can approve or reject reimbursements.</p>
                  )}
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Purchase detail drawer */}
      <Sheet open={!!selectedPurchase} onOpenChange={() => setSelectedPurchase(null)}>
        <SheetContent className="w-[95vw] max-w-md max-h-[85vh] overflow-y-auto" aria-describedby={selectedPurchase ? "purchase-detail-description" : undefined}>
          <SheetHeader>
            <SheetTitle>Purchase details</SheetTitle>
            <SheetDescription id="purchase-detail-description" className="sr-only">
              View and approve or reject this purchase. Use Approve &amp; Pay to charge your card, or Mark as paid (Autopay) to record payment outside the app.
            </SheetDescription>
          </SheetHeader>
          {selectedPurchase && (
            <div className="mt-6 space-y-4">
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Item</span>
                  <span className="font-medium">{selectedPurchase.item_name || "Order Request"}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Type</span>
                  {getTypeBadge(selectedPurchase.type)}
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Cleaner</span>
                  <span>{selectedPurchase.cleaner_name || "Unknown"}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Amount</span>
                  <span className="font-medium">
                    ${displayAmount(selectedPurchase).toFixed(2)}
                    {(selectedPurchase as { autopay_at?: string }).autopay_at && (
                      <span className="block text-xs text-muted-foreground font-normal">
                        Paid (Autopay)
                      </span>
                    )}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Status</span>
                  {getStatusBadge(selectedPurchase.status, selectedPurchase.payment_failed, reimbursementMode === "auto_pay")}
                </div>
                {selectedPurchase.receipt_url && (
                  <div className="pt-2 border-t">
                    <span className="text-sm text-muted-foreground">Receipt</span>
                    <button type="button" onClick={() => setReceiptImage(selectedPurchase.receipt_url)} className="block mt-2">
                      <img src={selectedPurchase.receipt_url!} alt="Receipt" className="rounded-lg max-h-40 w-full object-contain bg-muted" />
                    </button>
                  </div>
                )}
              </div>
              {selectedPurchase.delivery_status === "ordered" && canPerformNonPaymentActions && (
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setMarkDeliveredPurchase(selectedPurchase)}
                  disabled={markDelivered.isPending}
                >
                  <Truck className="w-4 h-4 mr-2" />
                  Mark delivered (upload proof)
                </Button>
              )}
              {selectedPurchase.status === "pending_approval" && selectedPurchase.type === "cleaner_purchase" && (
                <div className="flex flex-col gap-2 pt-4 border-t">
                  {paymentError && (
                    <div className="flex items-center gap-2 p-3 rounded-md bg-destructive/10 text-destructive text-sm">
                      <AlertCircle className="w-4 h-4 flex-shrink-0" />
                      {paymentError}
                    </div>
                  )}
                  {autopaySkipReason && (
                    <div className="flex items-center gap-2 p-3 rounded-md bg-amber-50 text-amber-800 text-sm">
                      <AlertCircle className="w-4 h-4 flex-shrink-0" />
                      {autopaySkipReason}
                    </div>
                  )}
                  {canManagePaymentMethodAndToggles && (
                    <p className="text-xs text-muted-foreground">
                      If card charge fails, use &quot;Mark as paid (Autopay)&quot; to record payment outside the app; it will show on the invoice as $0.00 for recordkeeping.
                    </p>
                  )}
                  <div className="flex flex-wrap gap-2">
                    {canPerformNonPaymentActions && (
                      <Button
                        variant="outline"
                        className="flex-1 min-w-[120px] text-red-600"
                        onClick={() => handleStatusChange(selectedPurchase.id, "rejected")}
                        disabled={updateStatus.isPending || payingPurchaseId === selectedPurchase.id || autopayingPurchaseId === selectedPurchase.id || tryAutopayPurchaseId === selectedPurchase.id}
                      >
                        <X className="w-4 h-4 mr-2" />
                        Reject
                      </Button>
                    )}
                    {canManagePaymentMethodAndToggles && inventoryAutopayEnabled && (
                      <>
                        <Button
                          variant="outline"
                          className="flex-1 min-w-[120px]"
                          onClick={() => handleTryAutopay(selectedPurchase.id)}
                          disabled={updateStatus.isPending || payingPurchaseId === selectedPurchase.id || autopayingPurchaseId === selectedPurchase.id || tryAutopayPurchaseId === selectedPurchase.id || reimbursementMode === "disabled" || !billingProfile?.default_payment_method_id}
                        >
                          {tryAutopayPurchaseId === selectedPurchase.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
                          Try autopay
                        </Button>
                        <Button
                          variant="outline"
                          className="flex-1 min-w-[120px]"
                          onClick={() => handleMarkAutopay(selectedPurchase.id)}
                          disabled={updateStatus.isPending || payingPurchaseId === selectedPurchase.id || autopayingPurchaseId === selectedPurchase.id || tryAutopayPurchaseId === selectedPurchase.id || reimbursementMode === "disabled"}
                        >
                          {autopayingPurchaseId === selectedPurchase.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
                          Mark as paid (Autopay)
                        </Button>
                      </>
                    )}
                    {canManagePaymentMethodAndToggles && (
                      <Button
                        className="flex-1 min-w-[120px]"
                        onClick={() => handleApproveAndPay(selectedPurchase.id)}
                        disabled={updateStatus.isPending || payingPurchaseId === selectedPurchase.id || autopayingPurchaseId === selectedPurchase.id || tryAutopayPurchaseId === selectedPurchase.id || !billingProfile?.default_payment_method_id || reimbursementMode === "disabled"}
                      >
                        {payingPurchaseId === selectedPurchase.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <DollarSign className="w-4 h-4 mr-2" />}
                        Approve & Pay
                      </Button>
                    )}
                    {!canPerformNonPaymentActions && !canManagePaymentMethodAndToggles && (
                      <p className="text-xs text-muted-foreground py-2">Only the owner can approve or pay.</p>
                    )}
                  </div>
                </div>
              )}
              {selectedPurchase.status === "pending_approval" && selectedPurchase.type === "request_order" && (
                <div className="pt-4 border-t">
                  {canPerformNonPaymentActions ? (
                    <div className="flex gap-2">
                      <Button variant="outline" className="flex-1" onClick={() => handleStatusChange(selectedPurchase.id, "rejected")} disabled={updateStatus.isPending}>
                        Dismiss
                      </Button>
                      <Button className="flex-1" onClick={() => handleStatusChange(selectedPurchase.id, "approved")} disabled={updateStatus.isPending}>
                        <Check className="w-4 h-4 mr-2" />
                        Mark as Ordered
                      </Button>
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">Only the owner or a team member with Full access can dismiss or mark as ordered.</p>
                  )}
                </div>
              )}
              {selectedPurchase.status === "approved" && canManagePaymentMethodAndToggles && (
                <div className="pt-4 border-t">
                  {paymentError && (
                    <div className="flex items-center gap-2 p-3 rounded-md bg-red-50 text-red-700 text-sm mb-2">
                      <AlertCircle className="w-4 h-4 flex-shrink-0" />
                      {paymentError}
                    </div>
                  )}
                  <Button
                    className="w-full"
                    onClick={() => handleApproveAndPay(selectedPurchase.id)}
                    disabled={payingPurchaseId === selectedPurchase.id || !billingProfile?.default_payment_method_id || reimbursementMode === "disabled"}
                  >
                    {payingPurchaseId === selectedPurchase.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <DollarSign className="w-4 h-4 mr-2" />}
                    Retry Payment
                  </Button>
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>

      <Dialog open={!!receiptImage} onOpenChange={() => setReceiptImage(null)}>
        <DialogContent className="max-w-2xl p-2">
          {receiptImage && (
            <img src={receiptImage} alt="Receipt" className="w-full h-auto max-h-[80vh] object-contain rounded-lg" />
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!markDeliveredPurchase}
        onOpenChange={(open) => {
          if (!open) {
            setMarkDeliveredPurchase(null);
            setMarkDeliveredProofFile(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Mark delivered</DialogTitle>
            <DialogDescription>Upload a proof photo. This will move the quantity from Ordered to In stock.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Proof photo</Label>
              {markDeliveredProofFile ? (
                <div className="flex items-center gap-2 p-3 bg-muted rounded-lg mt-1.5">
                  <Image className="w-5 h-5 text-muted-foreground" />
                  <span className="flex-1 truncate text-sm">{markDeliveredProofFile.name}</span>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setMarkDeliveredProofFile(null)}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <label className="flex items-center justify-center gap-2 h-12 border-2 border-dashed rounded-lg cursor-pointer hover:bg-muted/50 mt-1.5">
                  <Upload className="w-5 h-5 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Upload proof photo</span>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => setMarkDeliveredProofFile(e.target.files?.[0] ?? null)}
                  />
                </label>
              )}
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => { setMarkDeliveredPurchase(null); setMarkDeliveredProofFile(null); }}>Cancel</Button>
              <Button onClick={handleMarkDeliveredSubmit} disabled={!markDeliveredProofFile || markDelivered.isPending}>
                {markDelivered.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Truck className="w-4 h-4 mr-2" />}
                Mark delivered
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showCardModal} onOpenChange={(open) => { if (!open) { setShowCardModal(false); setCardSetupData(null); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Payment Method</DialogTitle>
            <DialogDescription>Enter your card details to pay invoices and reimbursements in Stravo.</DialogDescription>
          </DialogHeader>
          {cardSetupData && (() => {
            const stripePromise = cardSetupData.stripe_mode === "test" ? stripePromiseTest : stripePromiseLive;
            if (!stripePromise) {
              return (
                <p className="text-sm text-destructive">
                  {cardSetupData.stripe_mode === "test"
                    ? "VITE_STRIPE_TEST_PUBLISHABLE_KEY is not set. Add it for sandbox card setup."
                    : "VITE_STRIPE_PUBLISHABLE_KEY is not set for live mode."}
                </p>
              );
            }
            return (
              <Elements stripe={stripePromise}>
                <StripeCardForm
                  clientSecret={cardSetupData.clientSecret}
                  setupIntentId={cardSetupData.setupIntentId}
                  stripeMode={cardSetupData.stripe_mode}
                  onSuccess={handleCardSuccess}
                  onError={(msg) => toast.error(msg)}
                  savePaymentMethod={savePaymentMethod.mutateAsync}
                />
              </Elements>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
